import { format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import Head from 'next/head';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';
import { getFullImageUrl } from 'utils/image';
import type { ClinicDoctor, ClinicPage } from 'types/Clinic';
import type { ReviewStats } from 'types/Review';

type Props = {
  entry: ClinicPage;
  bestReview: Record<string, string>;
  reviewStats: ReviewStats;
};

type Members = {
  image: string[];
  name: string;
  url: string;
};

type ReviewSchema =
  | {
      review: {
        reviewRating?: {
          '@type': string;
          ratingValue: string;
          bestRating: string;
        };
        reviewBody?: string | undefined;
        datePublished?: string | undefined;
        author?: {
          '@type': string;
          name: string | undefined;
        };
        '@type': string;
      };
    }
  | {
      review?: undefined;
    };

type AggregateRating =
  | {
      aggregateRating: {
        '@type': string;
        ratingValue: string;
        reviewCount: number;
      };
    }
  | {
      aggregateRating?: undefined;
    };

type BestReview = {
  description?: string;
  date?: string;
  customerName?: string;
  overallRating?: number;
};

const clinicPageLink = ({ locale, slug }: Record<string, string>): string =>
  `https://${config.app.domains.proxy.production}/${locale}/${slug}`;

const schemaImage = (imageUrl: string, type: 'clinic' | 'doctor'): string[] => {
  const fullImageUrl = getFullImageUrl(imageUrl);

  if (type === 'clinic') {
    return [
      `${fullImageUrl}?fit=fill&w=550&h=300`,
      `${fullImageUrl}?fit=fill&w=900&h=500`,
      `${fullImageUrl}?fit=fill&w=1200&h=780`,
    ];
  }

  return [
    `${fullImageUrl}?fit=fill&w=140&h=140`,
    `${fullImageUrl}?fit=fill&w=280&h=280`,
    `${fullImageUrl}?fit=fill&w=370&h=370`,
  ];
};

const generateDoctorName = (doctor: ClinicDoctor): string => {
  const { firstName, lastName, postTitle, title } = doctor;

  return `${title ? title : ''} ${firstName} ${lastName}${
    postTitle && postTitle.trim() !== '' ? `, ${postTitle}` : ''
  }`;
};

const members = (doctors: ClinicDoctor[], locale: string): Members[] => {
  return doctors.map((ele) => {
    return {
      '@type': 'Person',
      image: ele.doctorImage?.url
        ? schemaImage(ele.doctorImage.url, 'doctor')
        : [],
      name: `${generateDoctorName(ele)} | Qunomedical`,
      url: `https://${config.app.domains.proxy.production}/${locale}/${ele.slug}`,
    };
  });
};

const reviewSchemaObj = (bestReview: BestReview): ReviewSchema => {
  const { customerName, date, description, overallRating } = bestReview || {};

  if (!bestReview) {
    return {};
  }

  return {
    review: {
      '@type': 'Review',
      ...(description || date || customerName
        ? {
            reviewBody: description,
            datePublished: format(parseISO(date as string), 'yyyy-MM-dd', {
              locale: enUS,
            }),
            author: {
              '@type': 'Person',
              name: customerName,
            },
          }
        : {}),
      ...(overallRating
        ? {
            reviewRating: {
              '@type': 'Rating',
              ratingValue: overallRating.toFixed(1),
              bestRating: overallRating.toFixed(1),
            },
          }
        : {}),
    },
  };
};

const aggregateRatingObj = (reviewStats: ReviewStats): AggregateRating => {
  const { avg_rating, total } = reviewStats || {};

  if (!avg_rating || !total) {
    return {};
  }

  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avg_rating.toFixed(1),
      reviewCount: total,
    },
  };
};

const StructuredData = ({
  entry,
  bestReview,
  reviewStats,
}: Props): JSX.Element => {
  const { locale } = useTranslation();
  const clinicPage = clinicPageLink({ locale, slug: entry.slug as string });
  const memberSchema = entry.doctors
    ? members(entry.doctors.items, locale)
    : null;

  const { city, location, photos } = entry || {};

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    name: entry.name,
    description: entry.metaDescription || '',
    isAcceptingNewPatients: true,
    image:
      photos && photos.items.length
        ? schemaImage(photos.items[0].url, 'clinic')
        : '',
    '@id': clinicPage,
    url: clinicPage,
    address: {
      '@type': 'PostalAddress',
      streetAddress: entry.address,
      addressLocality: city.name,
      addressCountry: city.country.name,
    },
    location: {
      '@type': 'Place',
      geo: {
        '@type': 'GeoCoordinates',
        latitude: location?.lat,
        longitude: location?.lon,
      },
    },
    medicalSpecialty: entry.medicalSpecialty || '',
    ...reviewSchemaObj(bestReview),
    ...aggregateRatingObj(reviewStats),
    ...(memberSchema
      ? {
          member: [...memberSchema],
        }
      : {}),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(baseSchema) }}
      />
    </Head>
  );
};

export default StructuredData;
